/**
 * <subscription-picker>
 *
 * Theme-owned replacement for the upstream Shopify subscriptions app block.
 *
 * Public API:
 *   Property:  selectedSellingPlanId   string | null
 *   Property:  selectedVariantId       string | null
 *   Method:    selectPlan(id)          Programmatic selection. Pass '' or null for one-time.
 *
 * Events:
 *   subscription:select          { variantId, sellingPlanId, allocationPrice }
 *   subscription:variantchange   { variantId }
 *
 * Styling API: see subscription-picker.liquid (CSS custom properties).
 *
 * Refs (declared via `ref="<name>"` attributes in the Liquid snippet):
 *   sellingPlanInput  hidden <input name="selling_plan">
 *   fieldset[]        one fieldset per variant
 *   errorMessage      <p> shown when require-selection and nothing chosen
 */

import { Component } from '@theme/component';

const HIDDEN_CLASS = 'subscription-picker__fieldset--hidden';
const RADIO_NAME_PREFIX = 'purchase_option';

const SUBSCRIPTION_SELECT_EVENT = 'subscription:select';
const SUBSCRIPTION_VARIANT_CHANGE_EVENT = 'subscription:variantchange';

/**
 * @typedef {Object} SubscriptionPickerRefs
 * @property {HTMLInputElement} [sellingPlanInput]
 * @property {HTMLFieldSetElement[]} [fieldset]
 * @property {HTMLElement} [errorMessage]
 */

/** @extends {Component<SubscriptionPickerRefs>} */
export class SubscriptionPicker extends Component {
  requiredRefs = ['sellingPlanInput'];

  #variantUnsubscribe = null;
  #activeVariantId = null;

  connectedCallback() {
    super.connectedCallback();

    const initial = this.dataset.initialVariantId;
    if (initial) this.#activeVariantId = initial;

    this.#applyVariantVisibility();
    this.#syncHiddenInputFromActiveRadio();

    this.addEventListener('change', this.#onChange);

    this.#subscribeToVariantChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.#variantUnsubscribe?.();
    this.#variantUnsubscribe = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#applyVariantVisibility();
    this.#syncHiddenInputFromActiveRadio();
  }

  // --- Public API ----------------------------------------------------------

  /** @returns {string | null} */
  get selectedSellingPlanId() {
    const value = this.refs.sellingPlanInput?.value;
    return value ? value : null;
  }

  /** @returns {string | null} */
  get selectedVariantId() {
    return this.#activeVariantId;
  }

  /**
   * Programmatically select a plan in the active fieldset.
   * Pass '' or null to select the one-time radio (if present).
   * @param {string | null} id
   */
  selectPlan(id) {
    const fieldset = this.#activeFieldset();
    if (!fieldset) return;

    const targetValue = id ?? '';
    const radios = fieldset.querySelectorAll(
      `input[type="radio"][name^="${RADIO_NAME_PREFIX}"]`,
    );

    let matched = null;
    for (const radio of radios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      if (radio.value === targetValue) {
        matched = radio;
        break;
      }
    }
    if (!matched) return;

    matched.checked = true;
    this.#writeHiddenInput(matched.value);
    this.#hideError();
    this.#dispatchSelect(matched);
  }

  /**
   * Validate selection state. Returns true if valid (or selection not required).
   * Toggles the error message ref on failure.
   * @returns {boolean}
   */
  validate() {
    const required = this.dataset.requireSelection === 'true';
    if (!required) return true;

    const radio = this.#activeRadio();
    if (radio) {
      this.#hideError();
      return true;
    }

    this.#showError();
    return false;
  }

  // --- Internals -----------------------------------------------------------

  #subscribeToVariantChange() {
    if (typeof window.subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;

    this.#variantUnsubscribe = window.subscribe(
      window.PUB_SUB_EVENTS.variantChange,
      (event) => this.#onVariantChange(event),
    );
  }

  #onVariantChange = (event) => {
    const variant = event?.data?.variant;
    if (!variant?.id) return;

    const newId = String(variant.id);
    if (newId === this.#activeVariantId) return;

    this.#activeVariantId = newId;
    this.#applyVariantVisibility();
    this.#syncHiddenInputFromActiveRadio();
    this.#hideError();

    this.dispatchEvent(
      new CustomEvent(SUBSCRIPTION_VARIANT_CHANGE_EVENT, {
        bubbles: true,
        composed: true,
        detail: { variantId: newId },
      }),
    );
  };

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio') return;
    if (!target.name.startsWith(RADIO_NAME_PREFIX)) return;

    const sellingPlanId = target.value || '';
    this.#writeHiddenInput(sellingPlanId);
    this.#hideError();
    this.#dispatchSelect(target);
  };

  #dispatchSelect(radio) {
    const sellingPlanId = radio.value || '';
    const allocationPrice = radio.dataset.allocationPrice
      ? Number(radio.dataset.allocationPrice)
      : null;

    this.dispatchEvent(
      new CustomEvent(SUBSCRIPTION_SELECT_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          variantId: this.#activeVariantId,
          sellingPlanId: sellingPlanId || null,
          allocationPrice,
        },
      }),
    );
  }

  #writeHiddenInput(value) {
    const input = this.refs.sellingPlanInput;
    if (!input) return;
    if (input.value !== value) input.value = value;
  }

  #applyVariantVisibility() {
    const fieldsets = this.#fieldsets();
    if (!this.#activeVariantId) {
      fieldsets.forEach((fs, i) => fs.classList.toggle(HIDDEN_CLASS, i !== 0));
      return;
    }

    let matched = false;
    for (const fs of fieldsets) {
      const matches = fs.dataset.variantId === this.#activeVariantId;
      fs.classList.toggle(HIDDEN_CLASS, !matches);
      if (matches) matched = true;
    }

    if (!matched && fieldsets.length > 0) {
      this.#writeHiddenInput('');
    }
  }

  #syncHiddenInputFromActiveRadio() {
    const radio = this.#activeRadio();
    if (!radio) {
      this.#writeHiddenInput('');
      return;
    }
    this.#writeHiddenInput(radio.value || '');
  }

  #fieldsets() {
    const refs = this.refs.fieldset;
    if (Array.isArray(refs)) return refs;
    if (refs) return [refs];
    return [];
  }

  #activeFieldset() {
    for (const fs of this.#fieldsets()) {
      if (!fs.classList.contains(HIDDEN_CLASS)) return fs;
    }
    return null;
  }

  #activeRadio() {
    const fs = this.#activeFieldset();
    if (!fs) return null;
    const checked = fs.querySelector(
      `input[type="radio"][name^="${RADIO_NAME_PREFIX}"]:checked`,
    );
    return checked instanceof HTMLInputElement ? checked : null;
  }

  #showError() {
    const el = this.refs.errorMessage;
    if (!el) return;
    el.hidden = false;
  }

  #hideError() {
    const el = this.refs.errorMessage;
    if (!el) return;
    el.hidden = true;
  }
}

if (!customElements.get('subscription-picker')) {
  customElements.define('subscription-picker', SubscriptionPicker);
}

export {
  SUBSCRIPTION_SELECT_EVENT,
  SUBSCRIPTION_VARIANT_CHANGE_EVENT,
};
